/**
 * Empty → null normalization for the clearable club fields (#2960).
 *
 * Sibling of `profileFields.ts` (#2931): the exact same `'' || undefined` bug
 * on a different endpoint. `description`, `city`, `state`, `primary_sport` and
 * `secondary_sports` are all nullable columns on `clubs`
 * (0001_initial_schema.sql:83-92) and `UpdateClubSchema` now declares them
 * `.nullish()`. The `PUT /clubs/:slug` handler clears the underlying column
 * when it receives an explicit `null` (`bindings.push(description)` etc.).
 *
 * The clients used to emit `undefined` for an emptied field (`value || undefined`).
 * `JSON.stringify` DROPS `undefined` keys entirely, so the field never reached
 * the API, the handler's `if (field !== undefined)` gate was false, and the
 * column was never cleared — the emptied field reappeared on reload.
 *
 * `null` is the only sentinel that BOTH survives serialization AND reaches the
 * `.nullish()` schema branch. These two primitives are the single source of
 * truth for that mapping so the three edit surfaces
 * (`ClubEditPage`, `ClubSettingsGeneralPage`, and the native
 * `clubs/[slug]/settings/general`) can never drift apart again.
 *
 * Why primitives, not a fixed-field normalizer like `profileFields.ts`'s: the
 * club surfaces send heterogeneous subsets (only `ClubEditPage` sends sports),
 * and `city`/`state` carry an omit-vs-clear distinction the settings surfaces
 * express via a `meeting_mode` conditional — `undefined` means "untouched",
 * which an always-return-`null` object normalizer cannot represent. Per-field
 * primitives let each caller keep that conditional while sharing the mapping.
 *
 * Pure and import-free by design: imported at RUNTIME by both the web pages
 * (`lib/api/clubFields`) and the native screen (`@shared/lib/api/clubFields`,
 * Metro-resolved like `@shared/lib/api/profileFields`), so it must not pull in
 * any web-only module.
 */

/**
 * Map an editable text field to its wire value: a trimmed non-empty string
 * passes through, anything empty (or whitespace-only, or `undefined`/`null`)
 * becomes `null` so the API clears the column.
 */
export function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Map an editable list field: a non-empty array passes through, an empty or
 * absent array becomes `null` so the API clears the column.
 */
export function emptyArrayToNull<T>(value: readonly T[] | null | undefined): T[] | null {
  return value && value.length > 0 ? [...value] : null;
}
